import apiEndPoints from "../../apiconstants";
import { api, handleErrorResponse, responseArgs } from "../api";

export interface Report {
    id: string;
    name: string;
    embedUrl: string;
    webUrl: string;
    datasetId: string;
}

export type PbiAccessToken = {
    access_token: string;
    expires_in: number;
};

export type PbiEmbedToken = {
    embed_token: string;
    embed_token_expiration?: string | number;
    // Alternate expiry shapes: Power BI GenerateToken's raw field, or a lifetime in seconds.
    expiration?: string | number;
    expires_in?: number;
};

export type EvaluationsPresignedUrlPayload = {
    evaluation_id?: string;
    s3_key?: string;
    s3_keys?: string[];
};

export const analyticsApiSlice = api.injectEndpoints({
    endpoints: builder => ({
        getPbiAccessToken: builder.query<PbiAccessToken, void>({
            query: () => apiEndPoints?.getPbiAccessToken,
            transformResponse: (response: responseArgs) => response.data as PbiAccessToken,
            transformErrorResponse: handleErrorResponse,
        }),
        getPbiEmbedToken: builder.query<PbiEmbedToken, { reportId: string; }>({
            query: ({ reportId }) => `${apiEndPoints?.getpbiEmbedToken}/${reportId}`,
            transformResponse: (response: responseArgs) => response.data as PbiEmbedToken,
            transformErrorResponse: handleErrorResponse,
        }),
        getAnalyticsReportsList: builder.query<Report[], void>({
            query: () => `${apiEndPoints?.getAnalyticsReportsList}`,
            transformResponse: (response: responseArgs) => response.data as Report[],
            transformErrorResponse: handleErrorResponse,
        }),
        refreshReportsList: builder.mutation<Report[], { workspaceId: string, datasetId: string; }>({
            query: ({ workspaceId, datasetId }) => `https://api.powerbi.com/v1.0/myorg/groups/${workspaceId}/datasets/${datasetId}/refreshes`,
            transformResponse: (response: responseArgs) => response.data as Report[],
            transformErrorResponse: handleErrorResponse,
        }),
        getRefreshAccessToken: builder.query<PbiAccessToken, void>({
            query: () => apiEndPoints?.getRefreshAccessToken,
            transformResponse: (response: responseArgs) => response.data as PbiAccessToken,
            transformErrorResponse: handleErrorResponse,
        }),
        getAnalysticsSearch: builder.mutation<any, any>({
            query: (payLoad) => ({
                url: `${apiEndPoints?.getAnalysticsSearch}`,
                method: 'POST',
                body: payLoad
            }),
            transformResponse: (response: any) => response?.data,
            transformErrorResponse: handleErrorResponse,
        }),
        refreshAnalyticsReport: builder.mutation<any, { aiworker_id: string }>({
            query: (payload) => ({
                url: `${apiEndPoints?.refreshAnalyticsReport}`,
                method: 'POST',
                body: payload,
            }),
            transformResponse: (response: any) => response,
            transformErrorResponse: handleErrorResponse,
        }),
        fetchRefreshAccessToken: builder.mutation<any, { aiworker_id: string; }>({
            query: (payLoad) => ({
                url: `${apiEndPoints?.getRefreshAccessToken}`,
                method: 'POST',
                body: payLoad
            }),
            transformResponse: (response: any) => response?.data,
            transformErrorResponse: handleErrorResponse,
        }),
        getIntegrations: builder.mutation<any, any>({
            query: (payLoad) => ({
                url: apiEndPoints.integrationsUrl,
                method: 'POST',
                body: payLoad
            }),
            transformErrorResponse: handleErrorResponse,
            transformResponse: (response: responseArgs) => response.data,
        }),
        getEvaluationsPresignedUrl: builder.mutation<any, EvaluationsPresignedUrlPayload>({
            query: (payLoad) => ({
                url: apiEndPoints.getEvaluationsPresignedUrl,
                method: 'POST',
                body: payLoad,
            }),
            transformErrorResponse: handleErrorResponse,
            transformResponse: (response: any) => response,
        }),
        getPyAnalystEndpoints: builder.query<Record<string, unknown>, void>({
            query: () => apiEndPoints.getPyAnalystEndpoints,
            transformResponse: (response: responseArgs) => (response.data ?? {}) as Record<string, unknown>,
            transformErrorResponse: handleErrorResponse,
        }),
    })
});

export const { useGetPbiAccessTokenQuery, useGetPbiEmbedTokenQuery, useGetAnalyticsReportsListQuery,
    useRefreshReportsListMutation, useGetRefreshAccessTokenQuery, useGetAnalysticsSearchMutation, useRefreshAnalyticsReportMutation, useFetchRefreshAccessTokenMutation, useGetIntegrationsMutation, useGetEvaluationsPresignedUrlMutation, useGetPyAnalystEndpointsQuery
} = analyticsApiSlice;